import "./header.css";
import JDISearch from "@/components/search";
import NavItem from "./components/navItem/navItem";
import { getServerTranslation } from "@/i18n/index";
import { LANGUAGE_CONFIG } from "@/constants/languageConfig";
import HeaderLogo from "./components/headerLogo/headerLogo";
import LanguageLogo from "./components/languageLogo/headerLogo";
import PAGE_TYPE from "@/constants/pageType";
import AddressButton from "@/components/address/HeaderAddress";
import UserInfoButton from "./components/userInfoButton/userInfoButton";
import BenefitBarWrap from "./components/benefitBarWrap/BenefitBarWrap";
import Category from "./components/category";
import { JDIHeaderNewProps as BaseJDIHeaderNewProps } from "@/common-components-src/types/jdiHeaderNew";

interface JDIHeaderNewProps extends BaseJDIHeaderNewProps {
  isHeaderDisabled?: boolean;
}
import cookieUtils from "@/utils/cookieUtils";
import { cms_rootCateListAll, getCart, getPageAllData } from "@/apis/header/header.api";
import { getLoginInfoApi, getMindData } from "@/apis/login/login.api";
import loginEnum from "@/constants/loginConfig";
import { curPostCodeOrAddress } from "@/utils/addressRequestUtils";
import { supportedLngs } from "@/i18n/i18nConst";

// 服务端数据获取
const fetchHeaderServerData = async () => {
  try {
    // 并行获取所有需要的数据
    const [cartResult, navDataResult, mindDataResult, cateDataResult, postCodeResult, [_, userInfoResult]] = await Promise.all([
      getCart({
        verticalTag: "cn_ybxt_b2c",
        omittedDetail: true,
        cartuuid: cookieUtils.getCookie("VISITOR_UUID"),
        userActionId: "getCart",
      }),
      getPageAllData({
        pageType: 5,
        systemType: 1,
        verticalTag: "cn_ybxt_b2c",
        tenantCode: "__default__",
        terminal: 2,
        buld: "405",
        businessType: 2,
        pageCode: "uk_pc_header_nav",
        businessTag: "cn_ybxt_b2c",
      }),
      getMindData(loginEnum.MindScopeEnum.HOME_PAGE),
      cms_rootCateListAll({}),
      curPostCodeOrAddress(),
      getLoginInfoApi({ types: [6] }, { meta: { forceIgnoreLogin: true } }),
    ]);

    // 处理购物车数量
    let cartNum = 0;
    if (cartResult?.success && cartResult?.data) {
      cartNum = Number(cartResult.data?.data?.subtotal?.info?.["HJM-D#subtotal&number"]?.productNum || "");
    }

    // 处理导航数据
    let navItems = [];
    if (navDataResult?.success && navDataResult?.data) {
      const navData = navDataResult.data?.floors[0]?.moduleInstances?.[0]?.dataBatch?.nav;
      if (navData?.length) {
        navItems = navData
          .map((item: any) => {
            const link = item.url.jumpType === "custom" ? item.url.jumpValue.link : "";
            return item.name && link ? { name: item.name, link } : null;
          })
          .filter(Boolean);
      }
    }

    // 处理用户数据
    let userInfo = null;
    if (userInfoResult?.success && userInfoResult?.data) {
      userInfo = userInfoResult.data;
    }

    // 处理语言配置
    const langCookie = supportedLngs.includes(cookieUtils.getCookie("_lang")) ? cookieUtils.getCookie("_lang") : "en_GB";
    const langConfig = LANGUAGE_CONFIG.filter((lang) => lang.languageCode.includes(langCookie));

    // 处理平台心智数据
    let mindList = null;
    if (mindDataResult?.success && mindDataResult?.data) {
      mindList = mindDataResult?.data;
    }

    // 处理类目数据
    let categoryList: any[] = [];
    let isActive = null;
    if (cateDataResult?.success && cateDataResult?.data) {
      cateDataResult.data.forEach((firstLevel: any) => {
        if (firstLevel.childrenList.length > 0) {
          const newSecondLevel: any = {
            id: firstLevel.id,
            parentId: firstLevel.parentId,
            cateId: firstLevel.cateId,
            cateName: firstLevel.cateName,
            cateAlias: firstLevel.cateAlias,
            cateImageUri: firstLevel.cateImageUri,
            selectedIconUri: firstLevel.selectedIconUri,
            cateLevel: 1,
            language: firstLevel.language,
            childrenList: [],
            bloodIds: firstLevel.bloodIds,
            backgroundColor: firstLevel.backgroundColor,
          };

          firstLevel.childrenList.forEach((secondLevel: any) => {
            if (secondLevel.childrenList.length > 0) {
              secondLevel.childrenList.forEach((thirdLevel: any) => {
                thirdLevel.secondCateName = secondLevel.cateName;
                thirdLevel.secondCateId = secondLevel.cateId;
                thirdLevel.backgroundColor = secondLevel.backgroundColor;
                newSecondLevel.childrenList.push(thirdLevel);
              });
            }
          });

          if (newSecondLevel.childrenList.length > 0) {
            categoryList.push(newSecondLevel);
          }
        }
      });
      if (categoryList && categoryList.length > 0) {
        isActive = { lv1: categoryList[0].cateId };
      }
    }

    return {
      cartNum,
      navItems,
      mindList,
      userInfo,
      postCode: postCodeResult,
      langConfig: langConfig[0],
      categoryData: { categoryList, isActive },
    };
  } catch (error) {
    console.error("Error fetching server data for header:", error);
    if (error?.message === "NEXT_REDIRECT") {
      throw error;
    }
    return {
      cartNum: 0,
      postCode: "",
      navItems: [],
      mindList: [],
      userInfo: null,
      categoryData: null,
      langConfig: LANGUAGE_CONFIG[0],
    };
  }
};

const JDIHeaderNew = async ({ pageType, searchParams, isHeaderDisabled = false }: JDIHeaderNewProps) => {
  const serverData: any = await fetchHeaderServerData();
  const { t } = await getServerTranslation();
  const showNavItemLimit = 6;
  const keyword = searchParams?.k || "";
  const navItems = serverData?.navItems || [];
  const curLangConfig = serverData?.langConfig || LANGUAGE_CONFIG[0];
  const headerClass = isHeaderDisabled || pageType === PAGE_TYPE.HOME ? "jdi-header home" : "jdi-header";

  const getMainContainerStyle = () => {
    switch (pageType) {
      case PAGE_TYPE.ONLY_LOGO:
      case PAGE_TYPE.UNSUBSCRIBE:
        return { display: "none" };
      default:
        return {};
    }
  };

  const unsubscribeTag = () => {
    if (pageType === PAGE_TYPE.UNSUBSCRIBE) {
      return <div className="unsubscribe-txt">{t("profile.unsubscribe.title")}</div>;
    }
  };

  const navItemsComp = () => {
    return (
      navItems &&
      navItems.length >= showNavItemLimit && (
        <div
          className="header-main-nav-wrap"
          id="jdi-header-main-nav-wrap"
          style={pageType === PAGE_TYPE.HOME || pageType === PAGE_TYPE.NORMAL ? {} : { display: "none" }}
        >
          <div className="w">
            <div className="header-main-nav">
              <div className="nav-list-wrap">
                <ul className="nav-list">
                  {navItems.map((item, index) => (
                    <NavItem key={index} name={item.name} link={item.link} />
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    );
  };

  return (
    <div style={{ position: "relative", zIndex: 1000 }}>
      <div id="jdi-header" className={headerClass}>
        <BenefitBarWrap mindList={serverData?.mindList} />
        <div className="header">
          <div className="header-main-wrap">
            <div className="w">
              <div className="header-main" style={isHeaderDisabled ? { pointerEvents: 'none' } : {}}>
                <HeaderLogo pageType={pageType} isHeaderDisabled={isHeaderDisabled} />
                <div id="jdi-header-main-container" style={getMainContainerStyle()} className={isHeaderDisabled ? 'header--disabled' : ''}>
                  <Category pageType={pageType}  isHeaderDisabled={isHeaderDisabled} categoryData={serverData?.categoryData} />
                  <JDISearch defaultKeyword={keyword as string} />
                  <AddressButton serverPostCode={serverData?.postCode || ""} />
                  <LanguageLogo displayName={curLangConfig?.displayName} />
                </div>
                <div id="jdi-header-user-info" style={getMainContainerStyle()}>
                  <UserInfoButton serverData={serverData} disable={isHeaderDisabled} />
                </div>
                {unsubscribeTag()}
              </div>
            </div>
          </div>
          {navItemsComp()}
        </div>
      </div>
    </div>
  );
};

export default JDIHeaderNew;
